import { useEffect, useRef } from 'react';
import { EventBus, useGameStore } from '@campus/core';

/**
 * PlayerNameplates — Overlays world-space : noms au-dessus des avatars.
 *
 * PATTERN : RAF + mutation DOM directe + callback refs O(1)
 *
 * Pourquoi pas setState pour les positions ?
 * - setState déclenche le cycle de réconciliation React
 * - 30 joueurs × 60fps = 1800 setState/seconde → mort du framerate
 *
 * Pourquoi pas querySelector dans le RAF ?
 * - querySelector = O(n) traversée DOM à chaque frame
 * - 30 joueurs × 60fps = 1800 traversées DOM/seconde
 * - Remplacé par une Map<userId, HTMLElement> mise à jour uniquement
 *   au montage/démontage (callback refs) → O(1) garanti
 */
export function PlayerNameplates() {
  // Positions reçues de Phaser via EventBus — jamais dans le state React
  const positionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  // Dictionnaire O(1) des nœuds DOM réels — mis à jour par callback refs
  const domNodesRef = useRef<Map<string, HTMLElement>>(new Map());

  const nearbyPlayers = useGameStore((s) => [...s.nearbyPlayers.values()]);

  // ── RAF loop : applique les positions via transform direct ────
  useEffect(() => {
    const handlePositions = (positions: Map<string, { x: number; y: number }>) => {
      positionsRef.current = positions;
    };

    EventBus.on('overlay:positions-updated', handlePositions);

    let rafId: number;
    const updateDOM = () => {
      positionsRef.current.forEach((pos, userId) => {
        // O(1) — Map lookup, aucune traversée DOM
        const el = domNodesRef.current.get(userId);
        if (el) {
          // calc() préserve le centrage horizontal (-50% de la largeur du nameplate)
          // et le placement au-dessus du sprite (-100% de sa hauteur)
          el.style.transform = `translate(calc(${pos.x}px - 50%), calc(${pos.y}px - 100%))`;
        }
      });
      rafId = requestAnimationFrame(updateDOM);
    };

    rafId = requestAnimationFrame(updateDOM);

    return () => {
      cancelAnimationFrame(rafId);
      EventBus.off('overlay:positions-updated', handlePositions);
    };
  }, []);

  return (
    <div
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {nearbyPlayers.map((player) => (
        <div
          key={player.userId}
          // Callback ref : enregistre le nœud DOM au montage, le supprime au démontage
          ref={(node) => {
            if (node) domNodesRef.current.set(player.userId, node);
            else domNodesRef.current.delete(player.userId);
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            // willChange: transform → hint GPU pour le compositing
            willChange: 'transform',
            // Centrer horizontalement le nameplate sur l'avatar
            transform: 'translate(-50%, -100%)',
          }}
        >
          <PlayerNameplate displayName={player.displayName} />
        </div>
      ))}
    </div>
  );
}

// ── Sous-composant de rendu ───────────────────────────────────

function PlayerNameplate({ displayName }: { displayName: string }) {
  return (
    <div
      style={{
        background: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontFamily: 'var(--font-body)',
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      {displayName}
    </div>
  );
}
